import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import {
    BuyOutDecision,
    IntercompanyDecisionCodeRequest,
    IntercompanyPreviewGroup,
    IntercompanyPreviewKind,
    IntercompanyPreviewLine,
    ReturnItem,
} from 'returnCenter-api';
import { intercompanyService } from 'services/intercompanyService';
import { companyChanged } from 'store/actions/companyActions';
import type { RootState } from 'store';

export type LiveDecision = { problem: string | null; awaitingReasonCode: boolean };

type InitialState = {
    preview: IntercompanyPreviewGroup[];
    loading: boolean;
    failed: boolean;

    requestedReturnId: number | null;

    /**
     * Suggestions resolved live as the operator edits. Kept apart from the preview rather than
     * merged into it, which would lose the prices and warnings that came with it.
     */
    liveSuggestions: Record<number, number | null>;

    /** What the supplying company ends up seeing per row, and why it will not do. */
    liveReasonCodes: Record<number, { effective: number; problem: string | null }>;

    liveDecisions: Record<number, LiveDecision>;
};

const initialState: InitialState = {
    preview: [],
    loading: false,
    failed: false,
    requestedReturnId: null,
    liveSuggestions: {},
    liveReasonCodes: {},
    liveDecisions: {},
};

/**
 * Kept in the store rather than in the panel because completing the return has to see it: a line
 * the supplying company cannot credit must stop the operator, and that check lives in
 * useReturnSlipActions.
 */
export const fetchIntercompanyPreview = createAsyncThunk(
    'intercompany/fetchPreview',
    async (returnId: number) => intercompanyService.getPreview(returnId),
);

/** Re-asks the supplying companies as the operator edits. No ERP behind it — see the service. */
export const resolveIntercompanyDecisionCodes = createAsyncThunk(
    'intercompany/resolveDecisionCodes',
    async (request: IntercompanyDecisionCodeRequest) => intercompanyService.resolveDecisionCodes(request),
);

const intercompanySlice = createSlice({
    name: 'intercompany',
    initialState,
    reducers: {
        clearIntercompanyPreview: () => initialState,
    },
    extraReducers: (builder) => {
        builder.addCase(companyChanged, () => initialState);
        builder.addCase(fetchIntercompanyPreview.pending, (state, action) => {
            state.preview = [];
            state.requestedReturnId = action.meta.arg;
            state.loading = true;
            state.failed = false;
            state.liveSuggestions = {};
            state.liveReasonCodes = {};
            state.liveDecisions = {};
        });
        builder.addCase(fetchIntercompanyPreview.fulfilled, (state, action) => {
            if (action.meta.arg !== state.requestedReturnId) return;

            state.preview = action.payload;
            state.loading = false;
            state.failed = false;
            // The preview is the fresher answer for every row it covers.
            state.liveSuggestions = {};
            state.liveReasonCodes = {};
            state.liveDecisions = {};
        });
        builder.addCase(resolveIntercompanyDecisionCodes.fulfilled, (state, action) => {
            for (const result of action.payload) {
                if (result.returnItemRowId != null) {
                    state.liveSuggestions[result.returnItemRowId] = result.decisionCodeId ?? null;
                    state.liveReasonCodes[result.returnItemRowId] = {
                        effective: result.effectiveReasonCode ?? 0,
                        problem: result.reasonCodeProblem ?? null,
                    };
                    state.liveDecisions[result.returnItemRowId] = {
                        problem: result.decisionCodeProblem ?? null,
                        awaitingReasonCode: result.awaitingReasonCode ?? false,
                    };
                }
            }
        });
        builder.addCase(fetchIntercompanyPreview.rejected, (state, action) => {
            if (action.meta.arg !== state.requestedReturnId) return;

            // Prices are informational, so a failed fetch must not block the operator — but it
            // also means selectProblems can only report problems it actually saw.
            state.preview = [];
            state.loading = false;
            state.failed = true;
            state.liveSuggestions = {};
            state.liveReasonCodes = {};
            state.liveDecisions = {};
        });
    },
});

export const { clearIntercompanyPreview } = intercompanySlice.actions;

export const isCreditedIntercompanyRow = (item: ReturnItem) =>
    (item.quantityInReturn ?? 0) > 0 && !item.batchReturnId && !item.intercompanyReturnId;

export const intercompanyDecisionVerdict = (
    item: ReturnItem,
    previewLine: IntercompanyPreviewLine | undefined,
    live: LiveDecision | undefined,
): LiveDecision => {
    if (!isCreditedIntercompanyRow(item) || item.intercompanyDecisionCodeIsManual) {
        return { problem: null, awaitingReasonCode: false };
    }

    if (live) return live;

    return {
        problem: previewLine?.decisionCodeProblem ?? null,
        awaitingReasonCode: previewLine?.awaitingReasonCode ?? false,
    };
};

const selectReturnItemsById = createSelector([(state: RootState) => state.returns.returnSlip], (returnSlip) => {
    const byId = new Map<number, ReturnItem>();
    for (const group of returnSlip?.returnItems ?? []) {
        for (const line of group.orderLines ?? []) {
            for (const item of line.returnItems ?? []) {
                if (item.id != null) byId.set(item.id, item);
            }
        }
    }
    return byId;
});

/**
 * Lines the supplying company cannot credit as things stand. Memoized because it builds a new array
 * and useReturnSlipActions reads it on every render.
 *
 * A line still waiting for a reason code is not one of them: that is every fresh row, and counting
 * it would make the warning meaningless the day it matters.
 */
const selectProblems = createSelector(
    [
        (state: RootState) => state.intercompany.preview,
        (state: RootState) => state.intercompany.liveReasonCodes,
        (state: RootState) => state.intercompany.liveDecisions,
        selectReturnItemsById,
    ],
    (preview, liveReasonCodes, liveDecisions, itemsById) =>
        preview.flatMap((group) =>
            (group.lines ?? [])
                .flatMap((line) => {
                    const item = line.returnItemRowId != null ? itemsById.get(line.returnItemRowId) : undefined;
                    if (!item || !isCreditedIntercompanyRow(item)) return [];

                    const rowId = line.returnItemRowId as number;

                    // A purchase only fails on a bad reason code if it is actually bought.
                    if (group.kind === IntercompanyPreviewKind.BuyOut) {
                        const problem = item.buyOutDecision === BuyOutDecision.Buy ? line.reasonCodeProblem : null;
                        return problem ? [{ supplyingCompany: group.supplyingCompany ?? '', sku: line.sku ?? '', problem }] : [];
                    }

                    // The live answer supersedes the preview's, so a fix unblocks completion at
                    // once rather than after a reload.
                    const live = liveReasonCodes[rowId];
                    const reasonProblem = live ? live.problem : (line.reasonCodeProblem ?? null);
                    const decisionProblem = intercompanyDecisionVerdict(item, line, liveDecisions[rowId]).problem;

                    return [
                        {
                            supplyingCompany: group.supplyingCompany ?? '',
                            sku: line.sku ?? '',
                            problem: line.pricingProblem ?? reasonProblem ?? decisionProblem ?? '',
                        },
                    ];
                })
                .filter((entry) => entry.problem !== ''),
        ),
);

export const intercompanySelectors = {
    selectPreview: (state: RootState) => state.intercompany.preview,
    selectFailed: (state: RootState) => state.intercompany.failed,
    selectLiveSuggestions: (state: RootState) => state.intercompany.liveSuggestions,
    selectLiveReasonCodes: (state: RootState) => state.intercompany.liveReasonCodes,
    selectLiveDecisions: (state: RootState) => state.intercompany.liveDecisions,

    selectProblems,
};

export default intercompanySlice.reducer;
